import { useEffect, useState } from 'react';
import { NudosHoverText, NudosSearchbarDropdown, NudosTextInput } from '../../NudosComponents';
import { IcityDetails } from '../../../types/cities.types';
import { getCitiesByCountry } from '../../../services/cities.services';
import { TcomponentSize } from '../../../types/global';
import errorMessage from '../../../utils/errorMessage';
import './NudosCityDropdown.scss';

const NudosCityDropdown = ({
  countryId,
  countryCode,
  componentSize,
  updateCurrentValueCallback,
  currentValue,
  requestNewCityCallback,
  isDeactivated,
  handleBlur,
  showSkeletonLoader,
  errorText,
  customCityLabel,
  sortAlphabetically
}: {
  countryId?: number;
  countryCode?: string;
  componentSize?: TcomponentSize;
  updateCurrentValueCallback: (newValue?: IcityDetails) => void;
  currentValue?: IcityDetails;
  isFocused?: boolean;
  requestNewCityCallback?: () => void;
  isDeactivated?: boolean;
  handleBlur?: (inputText: string) => void;
  showSkeletonLoader?: boolean;
  errorText?: string;
  customCityLabel?: string;
  sortAlphabetically?: boolean;
}) => {
  const [initialData, setInitialData] = useState<IcityDetails>();
  const [loadingCities, setLoadingCities] = useState<boolean>();
  const [availableCities, setAvailableCities] = useState<IcityDetails[]>([]);
  const [cityText, setCityText] = useState('');

  const countryHasDefinedCities = !loadingCities && availableCities && availableCities?.length > 0;
  const cityPlaceholderForTextInput = !countryId ? 'Escoge primero el país' : 'Escribe la ciudad';
  const cityPlaceholderForDropdown = !countryId ? 'Escoge primero el país' : 'Escoge la ciudad';
  const cityLabel = customCityLabel || 'Ciudad';
  const cityErrorTextForTextInput =
    countryId && !currentValue?.name && !isDeactivated ? 'Escribe la ciudad' : errorText;
  const cityErrorTextForDropdown = countryId && !currentValue?.name ? 'Debes escoger la ciudad' : undefined;

  const getAvailableCitiesByCountry = async () => {
    if (!countryId && !countryCode) return;
    setLoadingCities(true);
    try {
      const response = await getCitiesByCountry(countryCode || countryId || '');
      if (sortAlphabetically) {
        const sortedResponse = response.sort((a, b) => a.name?.localeCompare(b.name || '') || 0);
        setAvailableCities(sortedResponse);
      } else {
        setAvailableCities(response);
      }
    } catch (error) {
      errorMessage();
    } finally {
      setLoadingCities(false);
    }
  };

  const getCityOptionUI = (
    isActive: boolean,
    rawBillingData: IcityDetails,
    clickOptionCallback?: (option: IcityDetails) => void
  ) => {
    const { id, name } = rawBillingData;
    const handleSelectOption = (option: IcityDetails) => {
      if (!isActive) return;
      updateCurrentValueCallback(option);
      clickOptionCallback && clickOptionCallback(option);
    };
    return (
      <div
        className="optionContainer cityOption"
        key={`cityOption${id}`}
        onClick={() => handleSelectOption(rawBillingData)}
      >
        <NudosHoverText
          onlyIfTruncated
          customClassName={!name ? 'empty' : undefined}
          text={name || 'Escoge la ciudad'}
          charactersLimit={50}
        />
      </div>
    );
  };

  const formDropdownOptionsCallback = (
    rawOptions: IcityDetails[],
    clickOptionCallback?: (option: IcityDetails) => void,
    searchString?: string
  ) => {
    const addNewCityOption = requestNewCityCallback ? (
      <div key="addOther" className="optionContainer addNewCityOption" onClick={requestNewCityCallback}>
        Solicitar agregar ciudad
      </div>
    ) : undefined;
    const noResultsOption = (
      <div className="optionContainer" key="loading">
        {searchString ? 'No hay resultados para tu búsqueda' : 'Cargando ...'}
      </div>
    );
    if (rawOptions.length === 0) {
      return addNewCityOption ? [noResultsOption, addNewCityOption] : [noResultsOption];
    }
    const cityOptions = rawOptions.map(rawBillingData => {
      return getCityOptionUI(true, rawBillingData, clickOptionCallback);
    });
    return addNewCityOption ? [...cityOptions, addNewCityOption] : cityOptions;
  };

  const formSelectedOptionUICallback = (selectedOption: IcityDetails) =>
    getCityOptionUI(false, selectedOption, undefined);

  const filterBySearchStringCallback = (search: string, rawOptions: IcityDetails[]) => {
    return rawOptions.filter(rawBillingData => {
      return rawBillingData?.name?.toLowerCase().trim().includes(search.toLowerCase().trim());
    });
  };

  useEffect(() => {
    if (initialData?.name || !currentValue?.name) return;
    setInitialData(currentValue);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentValue?.name]);

  useEffect(() => {
    // Updates the city selection when user writes in the text input for countries without defined cities
    if (cityText && !initialData?.name) return setInitialData({ name: cityText });
    if (!initialData?.name || countryHasDefinedCities) return; // To prevent deleting the initial data before the user modifies it
    updateCurrentValueCallback({ name: cityText });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cityText]);

  useEffect(() => {
    getAvailableCitiesByCountry();
    if (!initialData?.name) return; // To prevent deleting the initial data before the user modifies it
    // Reset the city selection upon changing countries
    updateCurrentValueCallback(undefined);
    setCityText('');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [countryId]);

  return (
    <div className="NudosCityDropdown">
      {countryHasDefinedCities && (
        <NudosSearchbarDropdown
          customClassname="NudosCityDropdownSearchbar"
          label={cityLabel}
          componentSize={componentSize}
          customPlaceholder={cityPlaceholderForDropdown}
          isFilled={!!currentValue}
          currentValue={currentValue}
          includeMagnifierIcon={false}
          rawOptions={availableCities}
          errorText={cityErrorTextForDropdown || errorText}
          formDropdownOptionsCallback={formDropdownOptionsCallback}
          formSelectedOptionUICallback={formSelectedOptionUICallback}
          filterBySearchStringCallback={filterBySearchStringCallback}
          isDeactivated={isDeactivated}
          handleBlur={handleBlur}
          showSkeletonLoader={loadingCities || showSkeletonLoader}
        />
      )}
      {!countryHasDefinedCities && (
        <NudosTextInput
          label={cityLabel}
          showSkeletonLoader={loadingCities || showSkeletonLoader}
          componentSize={componentSize}
          placeholder={cityPlaceholderForTextInput}
          isFilled={!!currentValue?.name}
          handleChange={setCityText}
          defaultValue={currentValue?.name || undefined}
          errorText={cityErrorTextForTextInput || errorText}
          isDeactivated={!countryId || isDeactivated}
          handleBlur={handleBlur}
        />
      )}
    </div>
  );
};

export default NudosCityDropdown;
